import { motion } from "framer-motion";
import { Leaf, CupSoda, CalendarCheck, BatteryCharging, BookOpen, UtensilsCrossed } from "lucide-react";

const CARDS = [
  {
    icon: Leaf,
    title: "Understand What Tea Can Do",
    text: "Green tea, herbal infusions, caffeine and ingredients worth knowing — explained honestly, without miracle claims.",
  },
  {
    icon: CupSoda,
    title: "Create Your Own Blends",
    text: "From the Citrus Green Tea Cooler to the Vanilla Rooibos Comfort Cup — mix signature blends that fit your taste and goals.",
  },
  {
    icon: CalendarCheck,
    title: "Follow a 30-Day Routine",
    text: "Start with the 7-Day Gentle Reset, build through the 14-Day Consistency Plan, then settle into a rhythm that lasts.",
  },
  {
    icon: BatteryCharging,
    title: "Hydration & Smarter Choices",
    text: "Simple beverage swaps and mindful drinking rituals that support your energy throughout the day.",
  },
  {
    icon: BookOpen,
    title: "170+ Pages of Depth",
    text: "25 chapters across 5 parts — hunger, habits, sleep, stress, movement and self-trust, all in plain language.",
  },
  {
    icon: UtensilsCrossed,
    title: "Meals That Satisfy",
    text: "Balanced plates with protein, fiber and volume — eating for satisfaction, not restriction.",
  },
];

export default function Benefits() {
  return (
    <section id="benefits" className="py-16 sm:py-24 lg:py-32" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs sm:text-sm font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-4">
            What you'll get inside
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A241E] leading-tight" data-testid="benefits-heading">
            A complete step-by-step guide to your personal tea routine
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="benefits-grid">
          {CARDS.map(({ icon: Icon, title, text }, i) => (
            <motion.article
              key={title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group bg-white rounded-2xl border border-[#1C3A27]/10 p-6 sm:p-8 shadow-[0_10px_30px_-18px_rgba(18,38,26,0.25)] hover:shadow-[0_24px_50px_-20px_rgba(18,38,26,0.35)] hover:border-[#D4A359]/50 transition-[box-shadow,border-color] duration-500"
              data-testid={`benefit-card-${i}`}
            >
              <div className="w-12 h-12 rounded-full bg-[#1C3A27] flex items-center justify-center mb-5 group-hover:bg-[#D4A359] transition-colors duration-500">
                <Icon className="w-5 h-5 text-[#FAF7F2] group-hover:text-[#12261A] transition-colors duration-500" strokeWidth={1.9} />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#1A241E] mb-2.5">{title}</h3>
              <p className="text-sm sm:text-base text-[#5C6A60] leading-relaxed">{text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
